import React from 'react';
import { Icons } from '../../constants';
// Import THEME_STYLES from theme constant file
import { THEME_STYLES as THEME_STYLES_CONST } from '../../constants';
import { Board } from '../../types';

interface BoardListProps {
    boards: Board[];
    isTrash: boolean;
    openMenuId: string | null;
    setOpenMenuId: (id: string | null) => void;
    onSelectBoard: (id: string) => void;
    onToggleFavorite: (id: string) => void;
    setEditingBoard: (board: Board) => void;
    setDeleteConfirmId: (id: string) => void;
    onRestoreBoard: (id: string) => void;
    onDeleteForever: (id: string) => void;
}

export const BoardList: React.FC<BoardListProps> = ({
    boards,
    isTrash,
    openMenuId,
    setOpenMenuId,
    onSelectBoard,
    onToggleFavorite,
    setEditingBoard,
    setDeleteConfirmId,
    onRestoreBoard,
    onDeleteForever
}) => {
    return (
        <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-sm border border-stone-100 dark:border-slate-800 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
            <table className="w-full text-left border-collapse">
                <thead>
                    <tr className="border-b border-slate-50 dark:border-slate-800 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 bg-slate-50/50 dark:bg-slate-800/30">
                        <th className="px-8 py-4 w-12 text-center">Fav</th>
                        <th className="px-4 py-4">Title & Description</th>
                        <th className="px-4 py-4">Last Edited</th>
                        <th className="px-4 py-4">Items</th>
                        <th className="px-8 py-4 text-right">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {boards.map(board => {
                        const theme = THEME_STYLES_CONST[board.themeColor || 'blue'] || THEME_STYLES_CONST['blue'];
                        return (
                            <tr key={board.id} onClick={() => !isTrash && onSelectBoard(board.id)} className="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer border-b border-slate-50 dark:border-slate-800 last:border-none">
                                <td className="px-8 py-5">
                                    <button onClick={(e) => { e.stopPropagation(); onToggleFavorite(board.id); }} className={`transition-colors ${board.isFavorite ? 'text-yellow-400' : 'text-slate-200 dark:text-slate-800 hover:text-slate-400'}`}>
                                        <Icons.Star className="w-5 h-5" fill={board.isFavorite ? 'currentColor' : 'none'} />
                                    </button>
                                </td>
                                <td className="px-4 py-5">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-3 h-3 rounded-full ${theme.text.replace('text-', 'bg-')} shadow-sm`}></div>
                                        <div>
                                            <h4 className="font-bold text-slate-800 dark:text-slate-100">{board.title}</h4>
                                            <p className="text-xs text-slate-400 dark:text-slate-500 truncate max-w-md">{board.description || 'No description'}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-5 font-medium text-slate-500 dark:text-slate-400 text-sm whitespace-nowrap">{board.lastEdited}</td>
                                <td className="px-4 py-5"><span className={`px-2 py-1 rounded-lg text-[10px] font-black ${theme.badge}`}>{board.itemCount ?? board.notes.length}</span></td>
                                <td className="px-8 py-5 text-right relative board-menu-container">
                                    <button onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === board.id ? null : board.id); }} className="w-8 h-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center transition-colors">
                                        <Icons.MoreHorizontal className="w-5 h-5 text-slate-400" />
                                    </button>
                                    {openMenuId === board.id && (
                                        <div className="absolute top-full right-8 mt-1 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-stone-100 dark:border-slate-700 p-1.5 z-50 animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
                                            {isTrash ? (
                                                <>
                                                    <button onClick={() => { onRestoreBoard(board.id); setOpenMenuId(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-green-600 dark:hover:text-green-400 rounded-lg transition-colors text-left"><Icons.RefreshCcw className="w-3.5 h-3.5" />Restore</button>
                                                    <button onClick={() => { onDeleteForever(board.id); setOpenMenuId(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-lg transition-colors text-left"><Icons.Trash className="w-3.5 h-3.5" />Delete Forever</button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => { setEditingBoard(board); setOpenMenuId(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-black dark:hover:text-white rounded-lg transition-colors text-left"><Icons.Edit className="w-3.5 h-3.5" />Edit Board</button>
                                                    <button onClick={() => { setDeleteConfirmId(board.id); setOpenMenuId(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-lg transition-colors text-left"><Icons.Trash className="w-3.5 h-3.5" />Move to Trash</button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
